import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, send, type MyStudent } from "../api.js";
import { useAsync } from "../useAsync.js";
import { booking, idempotencyKey } from "./api.js";
import { StudentBooking } from "./StudentDashboard.js";

/** The account owns the balance; the selected child owns only their lessons. */
export function FamilyBooking({ learners, initialStudentId, initialCreditId, onLearnerAdded }: {
  learners: MyStudent[];
  initialStudentId: string;
  initialCreditId?: string | null;
  onLearnerAdded: () => void;
}) {
  const [added, setAdded] = useState<Array<{id: string; fullName: string}>>([]);
  const [selectedId, setSelectedId] = useState(initialStudentId);
  // Undefined chooses an initial shared block; null is an explicit catalogue choice.
  const [blockId, setBlockId] = useState<string | null | undefined>(initialCreditId ?? undefined);
  const [adding, setAdding] = useState(false);
  const [fullName, setFullName] = useState("");
  const [under18, setUnder18] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attempt = useRef<{key: string; fullName: string; isUnder18: boolean} | null>(null);
  const submitting = useRef(false);
  const credits = useAsync(() => selectedId ? booking.credits(selectedId) : Promise.resolve(null), [selectedId]);
  const current = credits.data?.portal_student_id === selectedId ? credits.data : null;
  // Keep the attendee controls mounted while checking the next child's balance.
  // Cached display information never permits booking for an unchecked child.
  const lastFamilyBalance = useRef<typeof credits.data>(null);
  if (current) lastFamilyBalance.current = current;
  const blocks = (current ?? lastFamilyBalance.current)?.blocks.filter(block => block.family_shared) ?? [];
  // A supplied block is never silently replaced, including after exhaustion.
  const chosen = blockId === undefined
    ? blocks.find(block => block.booking.allowed && block.available_credits > 0)?.lesson_block_id ?? null
    : blockId;
  const selectedBlock = blocks.find(block => block.lesson_block_id === chosen);
  useEffect(() => {
    if (current && !credits.loading && !credits.error && blockId === undefined) setBlockId(chosen);
  }, [current, credits.loading, credits.error, chosen, blockId]);
  const options = [...learners.filter(learner => learner.privateBooking !== false),
    ...added.filter(learner => !learners.some(existing => existing.id === learner.id))];
  useEffect(() => {
    if (!selectedId && initialStudentId) setSelectedId(initialStudentId);
  }, [initialStudentId, selectedId]);
  const selectedName = options.find(learner => learner.id === selectedId)?.fullName;
  async function addChild(event: FormEvent) {
    event.preventDefault();
    if (submitting.current) return;
    const payload = {fullName: fullName.trim(), isUnder18: under18};
    if (!attempt.current) attempt.current = {key: idempotencyKey(), ...payload};
    if (attempt.current.fullName !== payload.fullName || attempt.current.isUnder18 !== payload.isUnder18) {
      setError("Retry the original details first so the same child is not added twice."); return;
    }
    submitting.current = true; setBusy(true); setError(null);
    try {
      const child = await send<{id: string; fullName: string}>("POST", "/family-setup/children", payload,
        {"Idempotency-Key": attempt.current.key});
      setAdded(previous => [...previous.filter(item => item.id !== child.id), child]);
      setSelectedId(child.id); setAdding(false); setFullName(""); attempt.current = null;
      onLearnerAdded();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not add the child.");
      // A definite validation/permission rejection made no record. Other
      // failures keep the retry key because the response may have been lost.
      if (cause instanceof ApiError && [400, 403, 404].includes(cause.status)) attempt.current = null;
    } finally { submitting.current = false; setBusy(false); }
  }
  if (!selectedId) return <>
    <p role="status">Choose a learner to book a lesson.</p>
    {options.length > 0 && <label htmlFor="family-booking-attendee">Who is attending?
      <select id="family-booking-attendee" value="" onChange={event => setSelectedId(event.target.value)}>
        <option value="" disabled>Choose a learner</option>
        {options.map(learner => <option key={learner.id} value={learner.id}>{learner.fullName}</option>)}
      </select>
    </label>}
  </>;
  if (credits.loading && !current && !lastFamilyBalance.current) return <p role="status">Checking your prepaid lessons…</p>;
  if (credits.error && !lastFamilyBalance.current) return <div className="panel"><p className="error" role="alert">{credits.error}</p>
    <button onClick={credits.reload}>Check again</button></div>;
  if (!current && !lastFamilyBalance.current) return null;
  // Existing student-owned and voucher purchases keep their original route.
  if (blocks.length === 0) return <>
    {options.length > 1 && <label>Who is attending?<select value={selectedId} onChange={event => setSelectedId(event.target.value)}>
      {options.map(learner => <option key={learner.id} value={learner.id}>{learner.fullName}</option>)}
    </select></label>}
    {credits.loading && !(chosen && current) && <p role="status">Checking your prepaid lessons…</p>}
    {credits.error && <p className="error" role="alert">{credits.error}<button onClick={credits.reload}>Check again</button></p>}
    {current && <StudentBooking key={`${selectedId}:${chosen ?? "catalogue"}`} studentId={selectedId}
      creditId={chosen} creditStudentId={selectedId} creditState={credits} onClearCredit={chosen ? () => setBlockId(null) : undefined} />}
  </>;
  return <>
    <section className="panel" aria-labelledby="family-attendee-heading">
      <h1 id="family-attendee-heading">Book a lesson</h1>
      <a className="small" href="/help/book-a-private-lesson" target="_blank" rel="noreferrer">Help with booking</a>
      {selectedBlock && current && <p role="status"><strong>{credits.loading || credits.error ? "Last checked: " : ""}{selectedBlock.available_credits} prepaid lessons remaining
        {selectedBlock.tutor_name ? ` with ${selectedBlock.tutor_name}` : ""}</strong> — {selectedBlock.service_name}
        {credits.loading && " · Checking the updated balance…"}
        {credits.error && " · This is the last successful balance read."}</p>}
      <p>Use your prepaid lessons for any child in your family. Each block stays with the tutor it was bought with.</p>
      <label>Which lessons?
        <select value={chosen ?? ""} disabled={busy || credits.loading} onChange={event => setBlockId(event.target.value || null)}>
          <option value="">All lesson options</option>
          {blocks.map(block => <option key={block.lesson_block_id} value={block.lesson_block_id}>
            {block.tutor_name ? `${block.service_name} with ${block.tutor_name}` : block.service_name} — {block.available_credits} remaining
          </option>)}
        </select>
      </label>
      <label htmlFor="family-booking-attendee">Who is attending?</label>
      <select id="family-booking-attendee" value={adding ? "new" : selectedId} disabled={busy}
        onChange={event => {
          if (event.target.value === "new") {setAdding(true); return;}
          setAdding(false); setSelectedId(event.target.value);
        }}>
        {options.map(learner => <option key={learner.id} value={learner.id}>{learner.fullName}</option>)}
        <option value="new">Add another child</option>
      </select>
      {credits.loading && !(chosen && current) && <p role="status">Checking your prepaid lessons…</p>}
      {credits.error && <p className="error" role="alert">{credits.error}<button onClick={credits.reload}>Check again</button></p>}
      {adding && <form onSubmit={event => void addChild(event)}>
        <label htmlFor="family-booking-child-name">Child’s full name</label>
        <input id="family-booking-child-name" required maxLength={120} value={fullName} disabled={busy}
          onChange={event => setFullName(event.target.value)} autoComplete="off" />
        <label><input type="checkbox" checked={under18} disabled={busy}
          onChange={event => setUnder18(event.target.checked)} /> The learner is under 18</label>
        {error && <p className="error" role="alert">{error}</p>}
        <button disabled={busy}>{busy ? "Adding…" : "Use this child"}</button>
      </form>}
      {!adding && <p className="small muted">The lesson will be recorded for {selectedName ?? "the selected child"}.</p>}
    </section>
    {!adding && current && <StudentBooking key={`${selectedId}:${chosen ?? "catalogue"}`} studentId={selectedId}
      creditId={chosen} creditStudentId={selectedId} creditState={credits} onClearCredit={chosen ? () => setBlockId(null) : undefined} />}
  </>;
}
